import { addUnreadMessages } from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import {
  setAlertRead,
  setAlertUnread,
  setAudibleNotificationIDs,
  setAlertQueueOrder,
  removeAlertQueueOrder,
  setLatestMsgTime,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import {
  ALERT_PREFIX,
  CHAT_RAPTOR_PREFIX,
  getAlertIdFromConversation,
  isValidAlertConversationID,
  isValidOrigin,
} from '@rsos/utils/talkJS';

/**
 * Util function to set an alert as read.
 * In the incidents state:
 * - Sets the alert's 'isUnread' status to false
 * - Removes the alert object from the 'unreadAlerts' array
 * @param {String} alertID - alert ID
 * @param {Function} dispatch - redux dispatch function
 */
export const handleReadAlert = (alertID, dispatch) => {
  dispatch(setAlertRead(alertID));
  dispatch(removeAlertQueueOrder(alertID));
};

/**
 *
 * @param {Object} session - TalkJS current session
 * @param {Object} alertsQueue - The alerts queue
 * @param {String} selectedAlertID - Selected alert ID
 * @param {Number} latestMsgTime - Latest alert message time
 * @param {Function} dispatch - redux dispatch function
 */
export const handleUnreadAlerts = (
  session,
  alertsQueue,
  selectedAlertID,
  latestMsgTime,
  dispatch,
) => {
  if (session?.unreads) {
    session.unreads.on('change', unreads => {
      const unreadConversations = [];
      let mostRecentUnreadAlert = null;

      // The TalkJS unreads array is triggered when the user is a participant of the conversation.
      // For example, if the user has accepted the alert, then any new unread messages would trigger
      // the unreads array. However, if the user has never clicked on the alert before, then the
      // unreads array does not get triggered.
      // Filters through the unreads array and pushes any unread alert conversations to the
      // unreadConversations array.
      unreads.forEach(unread => {
        const conversationID = unread.lastMessage.conversation.id;

        if (isValidAlertConversationID(conversationID)) {
          unreadConversations.push(unread.lastMessage);

          if (conversationID.startsWith(CHAT_RAPTOR_PREFIX)) {
            const alertID = getAlertIdFromConversation(
              unread.lastMessage.conversation,
            );

            dispatch(
              addUnreadMessages({
                alertID: alertID,
                messageID: unread.lastMessage.id,
              }),
            );
          }
        }
      });

      /**
       * Sort the unreadConversations from most recent to oldest.
       * @property {STRING} unreadConversations[idx].origin - determines the origin of the message
       */
      if (unreadConversations.length) {
        unreadConversations.sort((a, b) => b.timestamp - a.timestamp);

        if (getAlertIdFromConversation(unreadConversations[0].conversation)) {
          mostRecentUnreadAlert =
            alertsQueue[
              getAlertIdFromConversation(unreadConversations[0].conversation)
            ];
        }
      }

      // If the most recent conversation origin is not from rest, such as web, and it's not the
      // selected alert, then dispatch the unread alert to redux.
      if (
        unreadConversations.length &&
        isValidOrigin(unreadConversations[0]) &&
        mostRecentUnreadAlert &&
        mostRecentUnreadAlert?.alert_id !== selectedAlertID
      ) {
        dispatch(setAudibleNotificationIDs(mostRecentUnreadAlert.alert_id));
        dispatch(setAlertUnread(mostRecentUnreadAlert));
      }

      // Update the latest alert message time and set the alerts queue order.
      if (
        unreadConversations.length &&
        unreadConversations?.[0]?.timestamp > latestMsgTime
      ) {
        const alertsQueueOrder = unreadConversations
          .filter(
            message =>
              message.origin === 'web' &&
              alertsQueue[getAlertIdFromConversation(message.conversation)],
          )
          .map(alert => {
            return {
              alertID: getAlertIdFromConversation(alert.conversation),
              latestMsgTime: alert.timestamp,
            };
          });

        dispatch(setAlertQueueOrder(alertsQueueOrder));
        dispatch(setLatestMsgTime(unreadConversations[0].timestamp));
      }
    });
  }
};
